import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert
} from '@mui/material';
import {
  DocumentScanner as DocumentScannerIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import apiService from '../services/api';
import NeuroStats from '../components/neuro/NeuroStats';
import NeuroScanTester from '../components/neuro/NeuroScanTester';
import NeuroChatTester from '../components/neuro/NeuroChatTester';
import NeuroConfigPanel from '../components/neuro/NeuroConfigPanel';

/** Пресеты периода (как на дашборде) */
const PERIODS = [
  { value: '1d', label: '1 день', days: 1 },
  { value: '3d', label: '3 дня', days: 3 },
  { value: '7d', label: 'Неделя', days: 7 },
  { value: '30d', label: 'Месяц', days: 30 },
  { value: '365d', label: 'Год', days: 365 },
];

const formatPeriodDate = (date) =>
  date && !isNaN(date) ? format(date, 'd MMM yyyy', { locale: ruLocale }) : '';

const NeuroPage = () => {
  /** Период: по умолчанию неделя (как на дашборде) */
  const [period, setPeriod] = useState('7d');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const showError = useCallback((err) => {
    setSnackbar({
      open: true,
      message: err.response?.data?.message || err.message,
      severity: 'error'
    });
  }, []);

  const loadStats = useCallback(async (start, end) => {
    setStatsLoading(true);
    try {
      const formatDate = (d) => d.toISOString().split('T')[0];
      const response = await apiService.neuro.getStats(formatDate(start), formatDate(end));
      setStats(response.data?.data || null);
    } catch (err) {
      console.error('Ошибка загрузки статистики нейросети:', err);
      showError(err);
    } finally {
      setStatsLoading(false);
    }
  }, [showError]);

  const loadConfig = useCallback(async () => {
    try {
      const response = await apiService.neuro.getConfig();
      setConfig(response.data?.data || null);
    } catch (err) {
      console.error('Ошибка загрузки конфигурации нейросети:', err);
      showError(err);
    }
  }, [showError]);

  /** Загрузка статистики при смене периода */
  useEffect(() => {
    if (startDate && endDate && !isNaN(startDate) && !isNaN(endDate)) {
      loadStats(startDate, endDate);
    }
  }, [startDate, endDate, loadStats]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /**
   * Переключение пресета периода (как на дашборде)
   * @param {Object} event - событие изменения ToggleButtonGroup
   * @param {string} newPeriod - выбранный пресет периода
   */
  const handlePeriodChange = (event, newPeriod) => {
    if (!newPeriod) return;
    setPeriod(newPeriod);
    const preset = PERIODS.find((p) => p.value === newPeriod);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (preset.days - 1));
    setStartDate(start);
    setEndDate(end);
  };

  /** Ручной выбор дат сбрасывает пресет */
  const handleStartDateChange = (date) => {
    setPeriod(null);
    setStartDate(date);
  };
  const handleEndDateChange = (date) => {
    setPeriod(null);
    setEndDate(date);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const reloadStats = useCallback(() => {
    if (startDate && endDate && !isNaN(startDate) && !isNaN(endDate)) {
      loadStats(startDate, endDate);
    }
  }, [startDate, endDate, loadStats]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ruLocale}>
      {/** Заголовок и период (как на дашборде) */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{
          mb: { xs: 2, sm: 3 },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' }
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Нейросеть
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Данные за период: {formatPeriodDate(startDate)} - {formatPeriodDate(endDate)}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            color="primary"
            value={period}
            exclusive
            onChange={handlePeriodChange}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value} sx={{ px: 1.5 }}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1}>
            <DatePicker
              label="От"
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
            />
            <DatePicker
              label="До"
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
            />
          </Stack>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование сканирования и чата, метрики и конфигурация модели
      </Typography>

      <NeuroStats stats={stats} loading={statsLoading} />

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          variant="fullWidth"
        >
          <Tab icon={<DocumentScannerIcon />} iconPosition="start" label="Сканирование" />
          <Tab icon={<ChatIcon />} iconPosition="start" label="Чат" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Конфигурация" />
        </Tabs>
      </Paper>

      <Box sx={{ mb: 3 }}>
        {activeTab === 0 && (
          <NeuroScanTester onRequestDone={reloadStats} />
        )}
        {activeTab === 1 && (
          <NeuroChatTester onRequestDone={reloadStats} />
        )}
        {activeTab === 2 && (
          <NeuroConfigPanel config={config} />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default NeuroPage;
